import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

type Tone = "info" | "bad";
interface Toast {
  id: number;
  message: string;
  tone: Tone;
}

const ToastContext = createContext<(message: string, tone?: Tone) => void>(() => undefined);
const VISIBLE_MS = 4_500;
/**
 * At most this many at once: a sale says up to five things (saved, kitchen, receipt, drawer, mail;
 * the prototype shows them all), the oldest goes first.
 */
const MAX_TOASTS = 6;

/**
 * Short messages in the corner ("Bestelling ABCDE opgeslagen"); errors stay a little longer. On the phone
 * (the Tafo app) they sit at the bottom in the middle, and what went well is only a small black dot that
 * comes and goes, next to the others; what went wrong keeps its words in red (Furkan, 08/10/2026: "ipv
 * zoveel tekst kleine bolletjes naast elkaar die verschijnen en wegschijnen en dan bij rood wel tekst
 * tonen"). The words of a dot are still there for a screen reader.
 */
export function ToastProvider({
  children,
  max = MAX_TOASTS,
  visibleMs = VISIBLE_MS,
  place = "corner",
}: {
  children: ReactNode;
  max?: number;
  visibleMs?: number;
  place?: "corner" | "phone";
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const show = useCallback(
    (message: string, tone: Tone = "info") => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(Math.max(0, current.length - (max - 1))), { id, message, tone }]);
      setTimeout(
        () => {
          setToasts((current) => current.filter((toast) => toast.id !== id));
        },
        tone === "bad" ? visibleMs * 2 : visibleMs,
      );
    },
    [max, visibleMs],
  );

  const value = useMemo(() => show, [show]);
  return (
    <ToastContext value={value}>
      {children}
      {place === "phone" ? (
        <div className="toasts toasts-phone" role="status" aria-live="polite">
          {toasts
            .filter((toast) => toast.tone === "bad")
            .map((toast) => (
              <div key={toast.id} className="toast bad">
                {toast.message}
              </div>
            ))}
          <div className="toast-dots">
            {toasts
              .filter((toast) => toast.tone !== "bad")
              .map((toast) => (
                <span
                  key={toast.id}
                  className="toast-dot"
                  title={toast.message}
                  style={{ animationDuration: `${String(visibleMs)}ms` }}
                >
                  <span className="sr-only">{toast.message}</span>
                </span>
              ))}
          </div>
        </div>
      ) : (
        <div className="toasts" role="status" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className={`toast ${toast.tone === "bad" ? "bad" : ""}`}>
              {toast.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
